import { useCallback, useRef, useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { copyTextToClipboard } from '../../../../shared/utils/clipboard';
import {
  QC_TABLE_EMPTY_LABEL,
  formatQcTaskCellValue,
  formatQcTaskUserDisplayName,
  qcTaskUserRowHasDetailFields,
} from './qcTaskTableFormatters';

/**
 * Single-line CSS ellipsis for plain text cells (works with MRT column widths + `maxWidth: 0` on `td`).
 */
export function QcTaskEllipsisText({ children, title: titleProp }) {
  const str =
    children === null || children === undefined ? '' : String(children);
  const title =
    titleProp ??
    (str && str !== QC_TABLE_EMPTY_LABEL && str.length > 0 ? str : undefined);

  return (
    <Typography
      component="span"
      variant="body2"
      color={str === QC_TABLE_EMPTY_LABEL ? 'text.secondary' : 'text.primary'}
      title={title}
      sx={{
        display: 'block',
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {str}
    </Typography>
  );
}

/** Visible character cap before ellipsis (full string in tooltip via info icon). */
export const FILE_NAME_DISPLAY_MAX = 24;
export const REQ_ID_DISPLAY_MAX = 24;
export const ASSIGNED_REVIEWER_DISPLAY_MAX = 28;

const userTooltipLineSx = {
  display: 'block',
  fontSize: '0.8125rem',
  lineHeight: 1.4,
  wordBreak: 'break-word',
  color: '#ffffff',
  mb: 0.35,
  '&:last-of-type': { mb: 0 },
};

const userTooltipLinkSx = {
  ...userTooltipLineSx,
  textDecoration: 'underline',
  textUnderlineOffset: '2px',
  '&:hover': { color: '#ffffff' },
};

function QcUserDetailsTooltipBody({ user }) {
  if (!user || typeof user !== 'object') return null;
  const name = user.name != null ? String(user.name).trim() : '';
  const email = user.email != null ? String(user.email).trim() : '';
  const phoneRaw = user.phone ?? user.phone_number;
  const phone = phoneRaw != null ? String(phoneRaw).trim() : '';
  const authId =
    user.auth_user_id != null ? String(user.auth_user_id).trim() : '';
  const waDigits = phone ? phone.replace(/\D/g, '') : '';

  return (
    <Box
      component="span"
      sx={{
        display: 'block',
        textAlign: 'left',
        maxWidth: 280,
        py: 0.125,
        color: '#ffffff',
      }}
    >
      {name ? (
        <Typography component="span" variant="caption" sx={userTooltipLineSx}>
          {name}
        </Typography>
      ) : null}
      {email ? (
        <Typography
          component="a"
          variant="caption"
          href={`mailto:${email}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          sx={userTooltipLinkSx}
        >
          {email}
        </Typography>
      ) : null}
      {phone ? (
        <Typography
          component="a"
          variant="caption"
          href={`tel:${phone.replace(/\s/g, '')}`}
          onClick={(e) => e.stopPropagation()}
          sx={userTooltipLinkSx}
        >
          {phone}
        </Typography>
      ) : null}
      {waDigits ? (
        <Typography
          component="a"
          variant="caption"
          href={`https://wa.me/${waDigits}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          sx={userTooltipLinkSx}
        >
          whatsapp
        </Typography>
      ) : null}
      {authId ? (
        <Typography component="span" variant="caption" sx={userTooltipLineSx}>
          {authId}
        </Typography>
      ) : null}
    </Box>
  );
}

/**
 * Shows user display name (from API user object); info icon opens full user details (hover / tap).
 */
export function QcUserFieldWithInfo({ user, maxChars, infoAriaLabel }) {
  const display = formatQcTaskUserDisplayName(user);
  const hasDetails = qcTaskUserRowHasDetailFields(user);

  if (display === QC_TABLE_EMPTY_LABEL || !hasDetails) {
    return <QcTaskEllipsisText>{display}</QcTaskEllipsisText>;
  }

  const needsTruncate = display.length > maxChars;
  const shortened = needsTruncate ? `${display.slice(0, maxChars)}…` : display;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 0.25, sm: 0.5 },
        minWidth: 0,
        width: '100%',
      }}
    >
      <Tooltip
        title={<QcUserDetailsTooltipBody user={user} />}
        arrow
        placement="top"
        enterTouchDelay={0}
        disableInteractive={false}
        slotProps={{
          tooltip: {
            sx: {
              maxWidth: { xs: 'min(90vw, 300px)', sm: 300 },
              py: 1,
              px: 1.25,
              pointerEvents: 'auto',
              bgcolor: 'grey.900',
              color: '#ffffff',
              border: '1px solid',
              borderColor: 'grey.700',
              boxShadow: 3,
              '& .MuiTooltip-arrow': {
                color: 'grey.900',
              },
            },
          },
        }}
      >
        {/* `span` avoids nested <button> when MRT wraps the cell in click-to-copy (MRT_CopyButton). */}
        <IconButton
          component="span"
          size="small"
          aria-label={infoAriaLabel}
          edge="start"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          sx={{
            flexShrink: 0,
            p: { xs: 0.25, sm: 0.35 },
            '& .MuiSvgIcon-root': {
              fontSize: { xs: '1rem', sm: '1.125rem' },
            },
          }}
        >
          <InfoOutlinedIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
      <Typography
        component="span"
        variant="body2"
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
      >
        {shortened}
      </Typography>
    </Box>
  );
}

/**
 * Truncates long cell text; when truncated, shows an info icon — hover for full value, click copies it.
 */
export function QcTruncatedValueWithInfo({ value, maxChars, infoAriaLabel }) {
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef(null);

  const display = formatQcTaskCellValue(value);

  const handleCopyClick = useCallback(
    async (event) => {
      event.stopPropagation();
      event.preventDefault();
      const ok = await copyTextToClipboard(display);
      if (!ok) return;
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
      setCopied(true);
      copiedTimerRef.current = window.setTimeout(() => {
        setCopied(false);
        copiedTimerRef.current = null;
      }, 1600);
    },
    [display],
  );

  if (display === QC_TABLE_EMPTY_LABEL) {
    return <QcTaskEllipsisText>{display}</QcTaskEllipsisText>;
  }

  const needsTooltip = display.length > maxChars;
  const shortened = needsTooltip ? `${display.slice(0, maxChars)}…` : display;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 0.25, sm: 0.5 },
        minWidth: 0,
        width: '100%',
      }}
    >
      {needsTooltip ? (
        <Tooltip
          title={
            copied ? (
              <Typography component="span" variant="caption">
                Copied to clipboard
              </Typography>
            ) : (
              <Box
                component="span"
                sx={{ display: 'block', textAlign: 'left' }}
              >
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ display: 'block', wordBreak: 'break-word' }}
                >
                  {display}
                </Typography>
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  Click icon to copy
                </Typography>
              </Box>
            )
          }
          arrow
          placement="top"
          enterTouchDelay={0}
          open={copied ? true : undefined}
          slotProps={{
            tooltip: {
              sx: {
                maxWidth: { xs: 'min(90vw, 360px)', sm: 420 },
                wordBreak: 'break-word',
              },
            },
          }}
        >
          {/* `span` avoids nested <button> when MRT wraps the cell in click-to-copy (MRT_CopyButton). */}
          <IconButton
            component="span"
            size="small"
            aria-label={`${infoAriaLabel}. Click to copy full value.`}
            edge="start"
            onClick={handleCopyClick}
            sx={{
              flexShrink: 0,
              p: { xs: 0.25, sm: 0.35 },
              '& .MuiSvgIcon-root': {
                fontSize: { xs: '1rem', sm: '1.125rem' },
              },
            }}
          >
            <InfoOutlinedIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      ) : null}
      <Typography
        component="span"
        variant="body2"
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
      >
        {shortened}
      </Typography>
    </Box>
  );
}
